/* eslint-disable no-prototype-builtins */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector} from 'react-redux';
import {
  Divider,
  Grid,
  Switch,
  Button,
  Icon,
  FormHelperText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import JoditEditor from 'jodit-react';
import { changeAboutUsPageConfigConfig } from 'redux/actions/store_configs';
import { BackdropCircular, ImageGalleryDialog } from 'components';
import { assetManagerUrl } from 'configs';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit,
  uploadImageResolutionLimit
} from 'redux/actions/general';
import {
  uploadPrivateImageApi,
  getAssetManagerImagesApi
} from 'api';
import SelectOwnImage from './SelectOwnImage';


const useStyles = makeStyles({
  customized_error: {
    '& .jodit-container:not(.jodit_inline)': {
      border: '1px solid #FF3D57'
    },
    '& .jodit-placeholder': {
      color: '#FF3D57'
    }
  },
});

const config: unknown = {
  'minHeight': 100,
  'showXPathInStatusbar': false,
  'spellcheck': false,
  'askBeforePasteHTML': false,
  'askBeforePasteFromWord': false,
  'toolbar': false,
  'showCharsCounter': true,
  'showWordsCounter': false,
  'enter': 'BR',
  'placeholder': '*Text',
  'limitChars': '176'
};

const AboutUsPageCTAFull = ({values, onChange, errors}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { cta_full_section } = values;
  const [openImageDialog, toggleOpenImageDialog] = useState(false);
  const [ownImagesTagIds, setOwnImagesTagIds] = useState('');

  const {
    assetCategoryTags,
    currentCategoryId,
    assetImagesData,
    uploadingPrivateImage
  } = useSelector((state: IReduxState) => state.asset_manager);

  const {profile: {stores = []}} = useSelector((state: IReduxState) => state.user);

  const storeCode = stores.length && stores[0]?.storeCode;
  const {id: tagId, tagCode} = assetCategoryTags.find((tag) => tag.tagCode === 'about_us_page_cta_full_section') ||
                               {id: null, tagCode: null};
  const privateTagData = assetCategoryTags.find((tag) => tag.tagCode === 'private');
  const storeCodeTagData = assetCategoryTags.find((tag) => tag.tagCode === `${storeCode}`);

  useEffect(() => {
    if (tagId && privateTagData && storeCodeTagData) {
      setOwnImagesTagIds(`${tagId}, ${privateTagData.id}, ${storeCodeTagData.id}`);
    }
  }, [tagId, privateTagData, storeCodeTagData]);

  useEffect(() => {
    if (ownImagesTagIds.length) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 20,
        page: 1,
        tagIds: ownImagesTagIds,
        isPrivate: true
      });
    }
  }, [ownImagesTagIds]);
  const handleChange = (event) => {
    const {name, checked, type, value} = event.target;
    dispatch(changeAboutUsPageConfigConfig(name, checked, type, value, 'cta_full_section'));
  };

  const handleChangeContent = (value, name) => {
    dispatch(changeAboutUsPageConfigConfig(name, null, 'textarea', value, 'cta_full_section'));
  };

  const handleSelectImage = (data, selectImageConfigPath) => {
    const value = `${assetManagerUrl}/${data.webPath}`;
    dispatch(changeAboutUsPageConfigConfig(
      selectImageConfigPath,
      null,
      'text',
      value,
      'cta_full_section'
    ));
    onChange(`cta_full_section[${selectImageConfigPath}]`, value);
  };

  const handleUpLoadImage = async (event) => {
    event.persist();
    const {files, value} = event.target;
    const formData = new FormData();

    if (files.length) {
      const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
      const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
      const acceptableFormats = 'png|jpeg';
      const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

      const reader = new FileReader();

      reader.readAsDataURL(files[0]);
      reader.onload = (e) => {
        const image = new Image();

        image.src = e.target.result as string;
        image.onload = async () => {
          const {height, width} = image;

          if (!hasImageFile.test(files[0].type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
            dispatch(uploadFileTypeUnsupported());
          } else if (width !== 1660 && height !== 500) {
            dispatch(uploadImageResolutionLimit('1660 x 500px'));
          } else if (files[0].size > 524288) {
            dispatch(uploadFileSizeLimit('512kB'));
          } else {
            formData.append('image', files[0]);

            if (ownImagesTagIds.length) {
              formData.append('tags', `${tagCode},private,${storeCode}`);
            } else {
              const tags = [
                tagCode,
                privateTagData.tagCode,
                {
                  label: storeCode,
                  tagCode: storeCode,
                  category: currentCategoryId
                }
              ];
              formData.append('tags', JSON.stringify(tags));
            }

            const imageData = uploadPrivateImageApi({dispatch, formData, isAddingNewTag: !ownImagesTagIds.length});

            if (imageData) {
              handleSelectImage(imageData, 'store_info/about_us_page/cta_full_background_image');
            }
          }
        };
      };
    }
  };

  const isContentError = errors?.cta_full_section?.hasOwnProperty('store_info/about_us_page/cta_full_content');

  return (
    <div>
      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} sm={2} className="my-2 ml-4">Enable</Grid>
        <Grid item xs={12} sm={9}>
          <Switch
            color="primary"
            name={'store_info/about_us_page/is_cta_full_enabled'}
            checked={cta_full_section['store_info/about_us_page/is_cta_full_enabled'] == 1}
            onChange={(event) => {
              onChange('cta_full_section[\'store_info/about_us_page/is_cta_full_enabled\']', event.target.checked ? 1 : 0);
              handleChange(event);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">
          CTA Background Image<span className="text-error">*</span>
        </Grid>
        <Grid item xs={12} sm={9}>
          <div>
            <Button
              className="w-full max-w-300 p-0"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label htmlFor="upload-provider-image" className="flex items-center w-full justify-center px-5 py-2">
                <Icon className="mr-2">upload</Icon>
                Upload Own Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={handleUpLoadImage}
              onClick={(event) => (event.target as HTMLInputElement).value = null}
              id="upload-provider-image"
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            Files supported: png, jpeg, jpg. File size should be less than 512kB
          </FormHelperText>
          <Button
            className="w-full max-w-300 mt-2 mr-2"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => toggleOpenImageDialog(true)}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Library
          </Button>
          <SelectOwnImage
            title="Select CTA Image From Own Images"
            imagesTagIds={ownImagesTagIds}
            isOwnImages={assetImagesData?.[ownImagesTagIds]?.items?.length > 0}
            selectImageConfigPath="store_info/about_us_page/cta_full_background_image"
            handleSelectImage={handleSelectImage}
            cols={3}
            rowHeight={90}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="ml-4">
          Content Text <span className="text-error">*</span>
        </Grid>
        <Grid item xs={12} sm={9}>
          <div className={isContentError ? classes.customized_error : ''}>
            <JoditEditor
              value={cta_full_section['store_info/about_us_page/cta_full_content']}
              config={config}
              onChange={(newContent) => {
                const content = newContent.replace(/style="(.*?)"/gm, '');
                onChange('cta_full_section[\'store_info/about_us_page/cta_full_content\']', content);
                handleChangeContent(content, 'store_info/about_us_page/cta_full_content');
              }}
              onBlur={(newContent) => {
                const content = newContent.replace(/style="(.*?)"/gm, '');
                onChange('cta_full_section[\'store_info/about_us_page/cta_full_content\']', content);
                handleChangeContent(content, 'store_info/about_us_page/cta_full_content');
              }}
            />
          </div>
          {isContentError &&
            <FormHelperText error className="ml-3">
              {errors.cta_full_section['store_info/about_us_page/cta_full_content']}
            </FormHelperText>
          }
        </Grid>
        {tagId &&
          <ImageGalleryDialog
            title="Select CTA Background Image"
            open={openImageDialog}
            setOpen={toggleOpenImageDialog}
            onImageSelect={(data) => handleSelectImage(data, 'store_info/about_us_page/cta_full_background_image')}
            imageTagId={tagId}
            cols={3}
            rowHeight={90}
          />
        }
      </Grid>
      <Divider className="my-8" />
      <BackdropCircular loading={uploadingPrivateImage} />
    </div>
  );
};

export default AboutUsPageCTAFull;
